import React, { useState, useEffect } from "react";
import { FaCheck, FaEye } from "react-icons/fa";
import useApi from "../../hooks/useApi";
import apiClient, { initializeApiClient } from "../../api/apiClient";
import { toast } from "react-toastify";
import { CircularProgress } from "@mui/material";

export default function PaymentForm() {
  const initialState = {
    account_holder_name: "",
    w9_document: null,
    company_name: "",
    address: "",
    routing_number: "",
    city: "",
    bank_account_number: "",
    state: "",
    ssn_or_ein: "",
    zip_code: "",
    same_driver_licence: "",
  };

  const [payment, setPayment] = useState(initialState);
  const [w9_document, setW9_document] = useState(null);
  const [file, setFile] = useState();
  const [loading, setLoading] = useState(false);
  const [showW9Modal, setShowW9Modal] = useState(false);

  const handleChangePayment = (key, value) => {
    setPayment((prev) => ({ ...prev, [key]: value }));
    if (key === "w9_document" && value instanceof File) {
      setW9_document(URL.createObjectURL(value));
      setFile(value);
    }
  };

  useEffect(() => {
    fetchPaymentInfo();
  }, []);

  const mapFetchedDataToState = (data) => {
    return {
      account_holder_name: data.account_holder_name || "",
      w9_document: data?.w9_document || null,
      company_name: data.company_name || "",
      address: data.address || "",
      routing_number: data.routing_number || "",
      city: data.city || "",
      bank_account_number: data.bank_account_number || "",
      state: data.state || "",
      ssn_or_ein: data.ssn_or_ein || "",
      zip_code: data.zip_code || "",
      same_driver_licence: data.same_driver_licence || "",
      id: data.id || "",
    };
  };

  const fetchPaymentInfo = async () => {
    initializeApiClient();
    const response = await apiClient.get("/payment/bank-info/");
    if (response.status === 200) {
      const data = response.data;
      const mappedData = mapFetchedDataToState(data);
      setPayment(mappedData);
      if (data.w9_document) {
        setW9_document(data.w9_document);
      }
    }
  };
  const { request } = useApi((formData) =>
    apiClient.patch(`/payment/bank-info/${payment.id}/`, formData)
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const formData = createFormData();
      const result = await submitPaymentInfo(formData);
      handleSubmitResult(result);
    } catch (error) {
      console.error("Error submitting payment info:", error);
      if (error?.response?.data?.w9_document) {
        toast.error(error.response.data.w9_document[0]);
      } else {
        toast.error("An unexpected error occurred. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const createFormData = () => {
    const formData = new FormData();
    Object.entries(payment).forEach(([key, value]) => {
      if (key === "w9_document" && file instanceof File) {
        formData.append(key, file);
      } else if (key !== "w9_document") {
        formData.append(key, value);
      }
    });
    return formData;
  };

  const submitPaymentInfo = async (formData) => {
    return await request(formData);
  };

  const handleSubmitResult = (result) => {
    if (result.status === 200) {
      toast.success("Payment info updated");
    } else {
      toast.error("Error updating payment info");
    }
  };

  const handleViewW9 = () => {
    setShowW9Modal(true);
  };

  const W9Modal = () => (
    <div className="modal" style={{ display: showW9Modal ? "block" : "none" }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">W9 Document</h5>
            <button
              type="button"
              className="btn-close"
              onClick={() => setShowW9Modal(false)}
            ></button>
          </div>
          <div className="modal-body">
            <img
              src={w9_document}
              alt="W9 Document"
              style={{ maxWidth: "100%" }}
            />
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div>
      <form onSubmit={handleSubmit} encType="multipart/form-data">
        <div className="site-content h-100">
          <div className="p-4">
            <div className="site-content-body">
              <div className="input-fields">
                <div className="row justify-content-between">
                  <div className="input-field col-md-5 d-flex flex-column mb-1 fs-5">
                    <label
                      className="label blue-clr m-1"
                      htmlFor="account-holder-name"
                    >
                      Account Holder Name
                    </label>
                    <input
                      required
                      type="text"
                      value={payment.account_holder_name}
                      onChange={(e) =>
                        handleChangePayment(
                          "account_holder_name",
                          e.target.value
                        )
                      }
                      className="form-control m-1  px-2 py-1 fs-4"
                      id="account-holder-name"
                    />
                  </div>
                  <div className="input-field col-md-5 d-flex flex-column fs-5">
                    <label className="label blue-clr m-1" htmlFor="upload">
                      W9 Document Upload
                    </label>
                    <div className="d-flex align-items-center">
                      <label
                        className="licence_bg form-control m-1 px-2 py-1 fs-4 file-input text-center flex-grow-1"
                        htmlFor="upload"
                      >
                        {!w9_document ? (
                          <i className="bi bi-image-fill" />
                        ) : (
                          <div
                            className="file-uploaded-text"
                            style={{ padding: "6px 0px" }}
                          >
                            File uploaded
                          </div>
                        )}
                      </label>
                      {w9_document && (
                        <button
                          type="button"
                          className="btn btn-primary ms-2"
                          onClick={handleViewW9}
                        >
                          <FaEye />
                        </button>
                      )}
                    </div>
                    <input
                      hidden
                      onChange={(e) =>
                        handleChangePayment("w9_document", e.target.files[0])
                      }
                      type="file"
                      className="form-control m-1 px-2 py-1 fs-4"
                      id="upload"
                    />
                  </div>
                </div>
                <div className="row gap-1 gap-md-2 justify-content-between">
                  <div className="input-field col-md-5 d-flex flex-column mb-1 fs-5">
                    <label
                      className="label blue-clr m-1"
                      htmlFor="company-name"
                    >
                      Company Name
                    </label>
                    <input
                      required
                      type="text"
                      value={payment.company_name}
                      onChange={(e) =>
                        handleChangePayment("company_name", e.target.value)
                      }
                      className="form-control m-1 px-2 py-1 fs-4"
                      id="company-name"
                    />
                  </div>
                  <div className="input-field col-md-5 d-flex flex-column mb-4 fs-5">
                    <div className="d-flex flex-wrap align-items-center">
                      <label className="label blue-clr m-1" htmlFor="address">
                        Address
                      </label>
                    </div>
                    <input
                      required
                      type="text"
                      value={payment.address}
                      onChange={(e) =>
                        handleChangePayment("address", e.target.value)
                      }
                      className="form-control m-1 px-2 py-1 fs-4"
                      id="address"
                    />
                  </div>
                </div>
                <div className="row gap-1 gap-md-2 justify-content-between">
                  <div className="input-field col-md-5 d-flex flex-column mb-4 fs-5">
                    <label
                      className="label blue-clr m-1"
                      htmlFor="routing-number"
                    >
                      Routing Number
                    </label>
                    <input
                      required
                      value={payment.routing_number}
                      onChange={(e) =>
                        handleChangePayment("routing_number", e.target.value)
                      }
                      type="number"
                      className="form-control m-1 px-2 py-1 fs-4"
                      id="routing-number"
                    />
                  </div>
                  <div className="input-field col-md-5 d-flex flex-column mb-4 fs-5">
                    <label className="label blue-clr m-1" htmlFor="city">
                      City
                    </label>
                    <input
                      type="text"
                      value={payment.city}
                      onChange={(e) =>
                        handleChangePayment("city", e.target.value)
                      }
                      className="form-control m-1 px-2 py-1 fs-4"
                      id="city"
                    />
                  </div>
                </div>
                <div className="row gap-1 gap-md-2 justify-content-between">
                  <div className="input-field col-md-5 d-flex flex-column mb-4 fs-5">
                    <label
                      className="label blue-clr m-1"
                      htmlFor="bank-account"
                    >
                      Bank Account Number
                    </label>
                    <input
                      type="number"
                      value={payment.bank_account_number}
                      onChange={(e) =>
                        handleChangePayment(
                          "bank_account_number",
                          e.target.value
                        )
                      }
                      className="form-control m-1 px-2 py-1 fs-4"
                      id="bank-account"
                    />
                  </div>
                  <div className="input-field col-md-5 d-flex flex-column mb-4 fs-5">
                    <label className="label blue-clr m-1" htmlFor="state">
                      State
                    </label>
                    <input
                      type="text"
                      value={payment.state}
                      onChange={(e) =>
                        handleChangePayment("state", e.target.value)
                      }
                      className="form-control m-1 px-2 py-1 fs-4"
                      id="state"
                    />
                  </div>
                </div>
                <div className="row gap-1 gap-md-2 justify-content-between">
                  <div className="input-field col-md-5 d-flex flex-column mb-4 fs-5">
                    <label className="label blue-clr m-1" htmlFor="ssn">
                      SSN or EIN
                    </label>
                    <input
                      required
                      type="text"
                      value={payment.ssn_or_ein}
                      onChange={(e) =>
                        handleChangePayment("ssn_or_ein", e.target.value)
                      }
                      className="form-control m-1 px-2 py-1 fs-4"
                      id="ssn"
                    />
                  </div>
                  <div className="input-field col-md-5 d-flex flex-column mb-4 fs-5">
                    <label className="label blue-clr m-1" htmlFor="zip">
                      Zip Code
                    </label>
                    <input
                      type="text"
                      value={payment.zip_code}
                      onChange={(e) =>
                        handleChangePayment("zip_code", e.target.value)
                      }
                      className="form-control m-1 px-2 py-1 fs-4"
                      id="zip"
                    />
                  </div>
                </div>
              </div>
              <div className="select-applies d-flex flex-column align-items-center justify-content-center">
                {!loading ? (
                  <button
                    className="btn_bgnone save-changes blue-clr p-3 fs-4 border-0"
                    type="submit"
                  >
                    <span className="pe-2">Save Changes</span>
                    <FaCheck />
                  </button>
                ) : (
                  <div className="text-center mt-3">
                    <h1>
                      <CircularProgress />
                    </h1>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </form>
      <W9Modal />
    </div>
  );
}
